import type { GetRef } from 'antd/es/_util/type';
import type { DefaultOptionType, SelectProps } from 'antd/es/select';
import Select from 'antd/es/select';
import Skeleton from 'antd/es/skeleton';
import { useMemo, useRef } from 'react';

import { filterOption } from 'utils/antd/filters-option';
import { sortByText } from 'utils/antd/sorters';
import { capitalizeFirstLetter } from 'utils/capitalize-first-letter';

import type { ExternalCode } from 'api';
import { useGetDisbursementExternalCodes } from 'api';

import { useOrganisationId } from 'hooks/context';

const { Option } = Select;

type Props = {
  onChange:
    | ((
        value: string,
        option: DefaultOptionType | DefaultOptionType[] | undefined
      ) => void)
    | undefined;
  value: string;
  'data-testid'?: string;
} & SelectProps<string>;

export const PurposeCodeSelector = ({
  onChange = () => {},
  value,
  'data-testid': dataTestId,
  ...props
}: Props) => {
  const organisationId = useOrganisationId<number>();

  const selectRef = useRef<GetRef<typeof Select>>(null);

  const {
    data: { data: purposeCodes = [] } = {},
    isLoading,
    isSuccess
  } = useGetDisbursementExternalCodes(organisationId, {
    name: 'B2B Purpose Codes'
  });

  const sortedPurposeCodes = useMemo(() => {
    return isSuccess
      ? purposeCodes.sort((a, z) => sortByText(a.value, z.value))
      : [];
  }, [purposeCodes, isSuccess]);

  if (isLoading) {
    return <Skeleton />;
  }

  return (
    <Select<string>
      showSearch
      value={value}
      data-testid={dataTestId}
      getPopupContainer={(trigger) => trigger.parentNode}
      className="w-[160px]"
      popupMatchSelectWidth={false}
      filterOption={filterOption}
      onChange={(...data) => {
        selectRef.current?.blur();

        onChange(...data);
      }}
      ref={selectRef}
      {...props}
    >
      {sortedPurposeCodes?.map((purposeCode: ExternalCode) => {
        return (
          <Option index={purposeCode.value} key={purposeCode.value}>
            {capitalizeFirstLetter(purposeCode.value.toLowerCase())}
          </Option>
        );
      })}
    </Select>
  );
};
